<template>
  <section
    v-if="!isLoading"
    class="invoice-add-wrapper"
  >
    <b-row class="invoice-add">
      <b-col lg="8">
        <b-card>
          <div
            ref="bookingForm"
            class="repeater-form"
            :style="{ height: trHeight }"
          >
            <b-row
              v-for="(booking, index) in bookingList"
              :key="index"
              ref="bookingItemForm"
              class="pb-2"
            >
              <b-col
                cols="12"
                class="p-0"
              >
                <div class="d-flex border rounded">
                  <b-col
                    cols="11"
                    class="pt-1"
                  >
                    <cancelled-booking-voucher-date-source :booking="booking" />
                  </b-col>

                  <b-col
                    cols="1"
                    class="d-flex justify-content-end pt-1"
                    v-if="bookingList.length > 1"
                  >
                    <feather-icon
                      size="16"
                      icon="XIcon"
                      class="cursor-pointer"
                      @click="removeBooking(index)"
                    />
                  </b-col>
                </div>
              </b-col>
            </b-row>
          </div>
          <b-button
            size="sm"
            variant="primary"
            @click="addNewBooking"
          >
            Add Booking
          </b-button>
        </b-card>
      </b-col>

      <b-col lg="4">
        <cancelled-booking-summary-card @submitBooking="createNewBooking" />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCol,
  BForm,
  BFormCheckbox,
  BRow,
  BButton,
} from 'bootstrap-vue';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from '@vue/composition-api';
import { required } from '@validations';
import { ValidationObserver } from 'vee-validate';
import BookingAdjustFeeField from '@/modules/booking/components/BookingAdjustFee.vue';
import BookingApprovalPatient from '../components/BookingApprovalPatient.vue';
import BookingBaggage from '@/modules/booking/components/BookingBaggage.vue';
import BookingCompanyContact from '@/modules/booking/components/BookingCompanyContact.vue';
import BookingCruise from '@/modules/booking/components/BookingCruise.vue';
import BookingNote from '@/modules/booking/components/BookingNote.vue';
import BookingPassenger from '@/modules/booking/components/BookingPassenger.vue';
import BookingStaff from '@/modules/booking/components/BookingStaff.vue';
import CancelledBookingSummaryCard from '@/modules/booking/components/CancelledBookingSummaryCard.vue';
import BookingTaxi from '@/modules/booking/components/BookingTaxi.vue';
import CancelledBookingVoucherDateSource from '@/modules/booking/components/CancelledBookingVoucherDateSource.vue';
import BookingWayPoint from '@/modules/booking/components/BookingWayPoint.vue';
import router from '@/router';
import store from '@/store';
import useBooking from '@/modules/booking/composables/useBooking';
import useBookingSourceType from '@/modules/booking/composables/useBookingSourceType';
import useCompany from '@/modules/company/composables/useCompany';
import useGlobalSettings from '@/modules/setting/composables/useGlobalSettings';
import useUtils from '@/composables/useUtils';
import useBookingType from '../composables/useBookingType';
import useTransition from '@/composables/useTransition';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

export default {
  setup(_, ctx) {
    const { createCancelledBooking } = useBooking();
    const { getBookingSourceTypeList } = useBookingSourceType();
    const { getGlobalSetting } = useGlobalSettings();
    const { getCompanyFilterList } = useCompany();
    const { getBookingTypeList } = useBookingType();
    const { convertDateToUTC } = useUtils();
    const { trAddHeight, trHeight, trSetHeight, trTrimHeight } =
      useTransition();
    const toast = useToast();

    const BOOKED_FOR_DATE_UTC = new Date();
    const NEW_BOOKING = {
      bookedForDateUtc: BOOKED_FOR_DATE_UTC,
      bookingSourceTypeId: null,
      bookingTypeId: null,
      companyId: null,
      id: null,
      voucherNum: null,
    };

    const bookingList = computed(() => store.getters['booking/bookingList']);
    const companyList = computed(() => store.getters['company/companyFilterList']);
    const bookingForm = ref(null);
    const bookingFormRef = ref(null);
    const bookingItemForm = ref(null);
    const bookingType = ref(null);
    const bookingTypeList = ref([]);
    const globalSettings = ref(null);
    const isLoading = ref(false);

    onUnmounted(() => {
      window.removeEventListener('resize', initTrHeight);
    });
    window.addEventListener('resize', initTrHeight);

    const initTrHeight = () => {
      trSetHeight(150);
      nextTick(() => {
        trSetHeight(bookingForm.value.scrollHeight);
      });
    };

    const createNewBooking = async () => {
      const listOfBooking = store.getters['booking/bookingList'];

      const isValid = listOfBooking.every((booking) => {
        return booking.voucherNum !== null && booking.voucherNum !== '';
      });
      if (!isValid) {
        toast.error({
          component: ToastificationContent,
          props: {
            title: 'One or more booking is missing voucher number',
            icon: 'XCircleIcon',
          },
        });

        return;
      }

      const voucherNumList = listOfBooking.map((booking) => booking.voucherNum);
      const duplicates = voucherNumList.filter(
        (item, index) => voucherNumList.indexOf(item) !== index
      );
      if (duplicates.length > 0) {
        toast.error({
          component: ToastificationContent,
          props: {
            title: 'One or more booking has duplicate voucher number',
            icon: 'XCircleIcon',
          },
        });

        return;
      }

      // Format booking dates to UTC
      const bookingsFormatted = listOfBooking.map((b) => {
        return {
          ...b,
          bookedForDateUtc: convertDateToUTC(b.bookedForDateUtc),
        };
      });
      
      // Create new bookings
      const result = await createCancelledBooking(bookingsFormatted);

      if (result === null) return;

      router.push({ name: 'booking-list' });
    };

    const initBookingObj = () => {
      store.commit('booking/addBookingToBookingList', {
        ...NEW_BOOKING,
        bookingTypeId: bookingTypeList.value.find((bt) => bt.isCancelled).id,
        companyId: companyList.value.find((c) => c.isCancelled).id,
      });
    };

    const initData = async () => {
      isLoading.value = true;

      store.commit('booking/resetBookingList');

      const [globalSettingsData, bookingTypeListData] = await Promise.all([
        getGlobalSetting(),
        getBookingTypeList(),
        getCompanyFilterList(),
        getBookingSourceTypeList(),
      ]);

      globalSettings.value = globalSettingsData;
      bookingTypeList.value = bookingTypeListData;

      initBookingObj();

      isLoading.value = false;

      initTrHeight();
    };

    const removeBooking = (index) => {
      store.commit('booking/removeBookingFromBookingList', index);

      trTrimHeight(bookingItemForm.value[0].offsetHeight);
    };

    const addNewBooking = () => {
      bookingForm.value.style.overflow = 'hidden';

      store.commit('booking/addBookingToBookingList', {
        ...NEW_BOOKING,
        bookingTypeId: bookingTypeList.value.find((bt) => bt.isCancelled).id,
        companyId: companyList.value.find((c) => c.isCancelled).id,
      });

      nextTick(() => {
        trAddHeight(bookingItemForm.value[0].offsetHeight);
        setTimeout(() => {
          bookingForm.value.style.overflow = null;
        }, 350);
      });
    };

    initData();

    return {
      // Reactive
      bookingList,
      bookingForm,
      bookingItemForm,
      bookingType,
      isLoading,
      trHeight,
      bookingFormRef,

      // Method
      createNewBooking,
      removeBooking,
      addNewBooking,

      // Validator
      required,
    };
  },
  components: {
    BCard,
    BCardBody,
    BCol,
    BForm,
    BFormCheckbox,
    BRow,
    BButton,
    BookingAdjustFeeField,
    BookingApprovalPatient,
    BookingBaggage,
    BookingCompanyContact,
    BookingCruise,
    BookingNote,
    BookingPassenger,
    BookingStaff,
    CancelledBookingSummaryCard,
    BookingTaxi,
    CancelledBookingVoucherDateSource,
    BookingWayPoint,
    ValidationObserver,
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.taxi-vehicle-select .vs__selected {
  width: 95%;
}

.invoice-add-wrapper {
  .add-new-client-header {
    padding: $options-padding-y $options-padding-x;
    color: $success;

    &:hover {
      background-color: rgba($success, 0.12);
    }
  }
}
</style>
<style lang="scss" scoped>
@import '~@core/scss/base/pages/app-invoice.scss';
@import '~@core/scss/base/components/variables-dark';

.booking-header {
  .title {
    width: 115px;
  }
}

.form-item-section {
  background-color: $product-details-bg;
}

.form-item-action-col {
  width: 27px;
}

.repeater-form {
  // overflow: hidden;
  transition: 0.35s height;
}

.v-select {
  &.item-selector-title,
  &.payment-selector {
    background-color: #fff;

    .dark-layout & {
      background-color: unset;
    }
  }
}

.dark-layout {
  .form-item-section {
    background-color: $theme-dark-body-bg;

    .row .border {
      background-color: $theme-dark-card-bg;
    }
  }
}
</style>
